import React, { useCallback, useContext, useMemo } from 'react'
import moment from 'moment'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { areRatesLoading, BaseRatePackageView, getBaseRatePackagesView, isAvailableForPurchase, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import TextButton from 'components/Luxkit/Button/TextButton'
import WalledContent from 'components/Common/WalledContent'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import Caption from 'components/Luxkit/Typography/Caption'
import { getDepositAmountPercentage, isDepositAvailableOnOffer } from 'lib/payment/depositsUtils'
import SecureWithDepositTagAndModal from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndModal'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import useOfferAvailableRates from 'hooks/useOfferAvailableRates'
import Group from 'components/utils/Group'
import PriceRowValueDiscount from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscount'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { initialRooms } from 'lib/offer/occupancyUtils'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import ModalContext from 'contexts/ModalContext'
import AlwaysOnCheapestPriceDetailsMobile from './AlwaysOnCheapestPriceDetailsMobile'
import BodyText from 'components/Luxkit/Typography/BodyText'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import { generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import TAOCancellationPolicies from 'components/Common/CancellationPolicies/AlwaysOn/TAOCancellationPolicies'

interface Props {
  offer: App.Offer;
  packageView: OfferPackageOptionView;
  checkIn?: string;
  duration: number;
  rooms: Array<App.Occupants>;
  calendarsLoading?: boolean;
  previewMode: boolean;
  currency: string;
  baseRatePackagesView: Array<BaseRatePackageView>
  defaultDepositAmountPercentage: number;
  canViewLuxPlusBenefits: boolean;
  detailsModalMode: 'room-and-package' | 'room-only';
  hasMultipleCancellationPolicies: boolean;
  showPartialRefundPolicyLabel?: boolean;
  availableForPurchase?: boolean;
}

function AlwaysOnCheapestRateOptionMobile(props: Props) {
  const {
    offer,
    packageView,
    checkIn,
    duration,
    rooms,
    calendarsLoading,
    previewMode,
    currency,
    baseRatePackagesView,
    defaultDepositAmountPercentage,
    canViewLuxPlusBenefits,
    detailsModalMode,
    hasMultipleCancellationPolicies,
    showPartialRefundPolicyLabel,
    availableForPurchase,
  } = props

  const {
    package: pkg,
    bundledDiscountPercent,
    hotelDiscountPercent,
  } = packageView

  const showModal = useContext(ModalContext)

  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)
  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const checkOutMoment = useMemo(() => checkInMoment?.clone().add(duration, 'days'), [checkInMoment, duration])
  const checkOut = useMemo(() => checkOutMoment?.format(ISO_DATE_FORMAT), [checkOutMoment])

  const offerAvailableRates = useOfferAvailableRates(offer.id, {
    checkIn,
    checkOut,
    rooms: rooms ?? initialRooms,
  })

  const pkgAvailableRate = useMemo(() => offerAvailableRates.rates.find(rate => rate.packageUniqueKey === pkg.uniqueKey), [pkg, offerAvailableRates])
  const fetching = !!calendarsLoading || !!offerAvailableRates?.fetching
  const useDynamicCancellationPolicies = offer.property?.useDynamicCancellationPolicies ?? false
  const ignoreDynamicCancellationPolicy = pkg?.ignoreDynamicCancellationPolicy ?? false

  const dynamicHotelAvailable = ((useDynamicCancellationPolicies && !!pkgAvailableRate && checkIn && !fetching) || !useDynamicCancellationPolicies || !checkIn || fetching)
  const hotelAvailable = packageView.hotelAvailable && dynamicHotelAvailable
  const canPurchase = availableForPurchase && packageView.capacityValid && hotelAvailable
  const canBuyNow = !!checkIn && !!rooms && canPurchase && !fetching && packageView.fromCalendarInfo

  const shouldShowDiscountPercent = hotelDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const shouldShowBundledDiscountPercent = bundledDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const highestDiscountPercent = hotelDiscountPercent > bundledDiscountPercent ? hotelDiscountPercent : bundledDiscountPercent
  const shouldShowDiscount = !offer?.isDiscountPillHidden && (shouldShowDiscountPercent || shouldShowBundledDiscountPercent) && (canPurchase || previewMode) && !pkg.roomRate?.inclusionsHideValue

  const baseRatePackageView = useMemo(() => {
    return baseRatePackagesView.find(
      (packageItem) => packageItem.duration === pkg.duration && packageItem.id === pkg.id,
    )
  }, [baseRatePackagesView, pkg.duration, pkg.id])

  const baseRateValue = canViewLuxPlusBenefits ? baseRatePackageView?.memberValue : baseRatePackageView?.value
  const inclusionsAmount = (canViewLuxPlusBenefits ? pkg.totalInclusionsAmount : pkg.inclusionsAmount) ?? 0
  const pkgValueOrPkgMemberValue = canViewLuxPlusBenefits ? pkg.memberValue : pkg.value
  const packageViewHotelValueOrMemberHotelValue = (canViewLuxPlusBenefits ? packageView.hotelMemberValue : packageView.hotelValue) ?? 0

  // We use base rate cost to calculate new totalValue and discountPercent, using the isBaseRate flag on rate plan level.
  // This shows a bigger discount
  const valuedUpTo = useMemo(() => {
    if (baseRateValue && (baseRateValue + inclusionsAmount) > pkgValueOrPkgMemberValue) {
      const displayedHotelValue = baseRateValue + inclusionsAmount + (packageView.propertyFees ?? 0)
      const displayedHotelDiscountPercent = getValueOffPercent(displayedHotelValue, packageView.hotelPrice)

      if (displayedHotelDiscountPercent > highestDiscountPercent) {
        return { value: displayedHotelValue, percent: displayedHotelDiscountPercent }
      }
    }

    return { value: packageViewHotelValueOrMemberHotelValue, percent: highestDiscountPercent }
  }, [baseRateValue, inclusionsAmount, pkgValueOrPkgMemberValue, packageViewHotelValueOrMemberHotelValue, packageView.propertyFees, packageView.hotelPrice, highestDiscountPercent])

  const isDepositEnabled = isDepositAvailableOnOffer(offer, currency, checkInMoment, packageView.hotelTotal, packageView.package, pkgAvailableRate)

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView }),
  ], [offer, packageView])

  const onSignUpClick = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  return (
    <WalledContent enforced={offer.walledGarden} wall={(
      <Group fullHeight direction="vertical" verticalAlign="end">
        <TextButton kind="secondary" onClick={onSignUpClick}>Sign up to view exclusive rates</TextButton>
      </Group>
    )}
    >
      <Group direction="vertical" gap={16} data-testid={pkg.uniqueKey}>
        <Group direction="horizontal" gap={4} wrap="wrap">
          {shouldShowDiscount && <PriceRowValueDiscount
            type="estimate"
            discountPercentage={valuedUpTo.percent}
            data-testid="always-on-rate-option-discount"
          />}
          {isDepositEnabled && <SecureWithDepositTagAndModal
            pkgAvailableRate={pkgAvailableRate}
            offerType={offer.type}
            depositAmountPercentage={depositAmountPercentage}
            depositThresholds={offer.depositThresholds}
          />}
        </Group>
        <Group direction="vertical" gap={8}>
          {hasMultipleCancellationPolicies && (
            <BodyText variant="medium">
              <BodyText variant="medium" weight="bold" colour="highlight-secondary" as="span">
                {showPartialRefundPolicyLabel ? 'Partial refund' : 'Flexible cancellation'}
              </BodyText>
              {' '}
              options available
            </BodyText>
          )}
          {!hasMultipleCancellationPolicies && (
            <TAOCancellationPolicies
              offerId={offer.id}
              useDynamicCancellationPolicies={useDynamicCancellationPolicies && !ignoreDynamicCancellationPolicy}
              timezoneOffset={offer.property?.timezoneOffset}
              uniqueKey={pkg.uniqueKey}
              checkInDate={checkIn}
              checkOutDate={checkOut}
              occupants={rooms}
              cancellationPolicy={pkg?.roomRate?.cancellationPolicy}
              offerType={offer.type}
            />
          )}
          <LoyaltyPointsEarnWithBreakdownModal
            calculationRequests={pointsEarnCalculationRequests}
            calculationType={!checkIn ? 'estimate' : 'exact'}
          />
        </Group>
        {(canPurchase || previewMode) && <Caption variant="medium">
          <AlwaysOnCheapestPriceDetailsMobile
            rooms={rooms}
            checkIn={checkIn}
            packageView={packageView}
            offer={offer}
            canBuyNow={canBuyNow}
            valuedUpTo={valuedUpTo}
            detailsModalMode={detailsModalMode}
          />
          <LoadingIndicator visible={fetching} floating opaque />
        </Caption>}
        <PriceRowAgentHubCommission
          size="M"
          offerId={offer.id}
          productType={offer.productType}
          offerTotal={packageView.hotelPrice}
          packageId={pkg.id}
        />
      </Group>
    </WalledContent>
  )
}

export default withOfferPageAndReduxState((state, appState, ownProps: Partial<Props>) => ({
  rooms: state.rooms,
  checkIn: state.checkInDate,
  duration: state.duration,
  calendarsLoading: !!appState.calendar.calendarsLoading[ownProps.packageView!.package.uniqueKey] || areRatesLoading(appState, state),
  previewMode: appState.system.previewMode,
  currency: appState.geo.currentCurrency,
  baseRatePackagesView: getBaseRatePackagesView(appState, state),
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
  canViewLuxPlusBenefits: getCanViewLuxPlusBenefits(appState),
  availableForPurchase: isAvailableForPurchase(state, appState),
}), AlwaysOnCheapestRateOptionMobile)
